/**
 * The timeline's geometry, in one place with no imports.
 *
 * Separate from the component so the end-to-end drag specs can measure against the same
 * numbers the component draws with. Importing the component itself would drag Vite's
 * `import.meta.env` into a Node process; restating the numbers in the test means testing
 * a timeline that does not exist, which is what the first run of `timeline.e2e.spec.ts`
 * did — it computed drop positions from a 48px hour against a component that renders 56.
 */

/**
 * Fallback window, used until the user's own preference has loaded.
 *
 * 07:00–22:00 was hardcoded until someone pointed out that they go to the gym at six and
 * read at eleven — both invisible, because a block above or below the window renders
 * outside a container that clips. The window is now a preference, and `visibleWindow`
 * widens it further to cover anything actually scheduled.
 */
export const START_HOUR = 6;
export const END_HOUR = 23;
export const PX_PER_HOUR = 56;
/** Drops snap to this grid, so a block never starts at 14:03. */
export const SNAP_MINUTES = 15;

export interface DayWindow {
  startHour: number;
  endHour: number;
}

/**
 * The hours to draw: the user's window, widened to include everything on the day.
 *
 * The widening is the part that matters. A preference can be wrong — set once and then
 * outgrown, or simply narrower than a particular day turned out to be — and a block you
 * cannot see is worse than a timeline that scrolls a little further. Whatever the
 * setting says, everything scheduled is on screen.
 */
export function visibleWindow(
  preference: DayWindow,
  spans: readonly { startMinutes: number; endMinutes: number }[],
): DayWindow {
  let startHour = preference.startHour;
  let endHour = preference.endHour;

  for (const span of spans) {
    startHour = Math.min(startHour, Math.floor(span.startMinutes / 60));
    // Ceiling, so a block ending at 23:15 does not have its last quarter hour clipped.
    endHour = Math.max(endHour, Math.ceil(span.endMinutes / 60));
  }

  return {
    startHour: Math.max(0, Math.min(startHour, 23)),
    endHour: Math.min(24, Math.max(endHour, startHour + 1)),
  };
}

/**
 * Where "now" sits on the drawn timeline.
 *
 * A day planner that does not say what time it is makes you do the arithmetic yourself: you read
 * the clock, find the hour label, and work out how much of the day is left. The line is the
 * cheapest possible answer, and the reason every calendar has one.
 *
 * Returns null rather than a clamped position in the two cases where a line would lie:
 *
 * - **The day being viewed is not today.** Looking back at Tuesday, or ahead at Friday, there is
 *   no "now" on that grid to mark.
 * - **Now is outside the hours drawn.** At 03:40 with a 06:00–23:00 window, pinning the line to
 *   the top edge would claim it is six in the morning. Nothing is a truer statement than a line
 *   in the wrong place.
 */
export function nowOffset(input: {
  /** Milliseconds since the epoch. */
  now: number;
  /** The instant the viewed day begins, in the user's zone — `dayStart` from the day plan. */
  dayStart: number;
  window: DayWindow;
}): { minutes: number; top: number } | null {
  const minutes = (input.now - input.dayStart) / 60_000;

  // Outside this calendar day at all: a different day is on screen.
  if (minutes < 0 || minutes >= 24 * 60) return null;

  const from = input.window.startHour * 60;
  const to = input.window.endHour * 60;
  if (minutes < from || minutes > to) return null;

  return { minutes, top: ((minutes - from) / 60) * PX_PER_HOUR };
}
